'use client';

import React, { useState, useEffect } from 'react';
import { Check, Plus, ListMusic, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { usePlaylists, Playlist } from '@/hooks/usePlaylists';
import { motion, AnimatePresence } from 'framer-motion';
import { useI18n } from '@/lib/i18n';

interface AddToPlaylistDialogProps {
  episodeId: string;
  episodeTitle?: string;
  isOpen: boolean;
  onClose: () => void;
}

interface PlaylistItemProps {
  playlist: Playlist;
  isSelected: boolean;
  onToggle: () => void;
  episodeCount: number;
}

const PlaylistItem: React.FC<PlaylistItemProps> = ({ 
  playlist, 
  isSelected, 
  onToggle, 
  episodeCount 
}) => {
  const { t } = useI18n();

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className={`
        relative p-3 rounded-xl border cursor-pointer transition-all duration-200
        ${isSelected 
          ? 'bg-primary/10 border-primary/30 shadow-sm' 
          : 'bg-background hover:bg-muted/50 border-border/50'
        }
      `}
      onClick={onToggle}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          <div className={`
            w-10 h-10 rounded-lg flex items-center justify-center
            ${isSelected 
              ? 'bg-primary/20 text-primary' 
              : 'bg-muted text-muted-foreground'
            }
          `}>
            <ListMusic className="w-4 h-4" />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="font-medium text-foreground truncate">
              {playlist.name}
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Clock className="w-3 h-3" />
              <span>
                {episodeCount} {t('library.playlistDialog.episodesLabel', 'episodes')}
              </span>
              {playlist.description && (
                <>
                  <span>•</span>
                  <span className="truncate">{playlist.description}</span>
                </>
              )}
            </div>
          </div>
        </div>
        
        <div className={`
          w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all
          ${isSelected 
            ? 'bg-primary border-primary' 
            : 'border-muted-foreground/30'
          }
        `}>
          <AnimatePresence>
            {isSelected && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                transition={{ duration: 0.15 }}
              >
                <Check className="w-3 h-3 text-primary-foreground" />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
};

interface CreatePlaylistFormProps {
  onSubmit: (name: string, description?: string) => void;
  onCancel: () => void;
  isLoading: boolean;
}

const CreatePlaylistForm: React.FC<CreatePlaylistFormProps> = ({ 
  onSubmit, 
  onCancel, 
  isLoading 
}) => {
  const { t } = useI18n();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim()) {
      onSubmit(name.trim(), description.trim() || undefined);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="border border-border/50 rounded-xl p-4 bg-muted/30"
    >
      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label className="text-sm font-medium text-foreground block mb-1">
            {t('library.playlistDialog.nameLabel', 'Playlist Name *')}
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('library.playlistDialog.namePlaceholder', 'Enter playlist name...')}
            className="w-full px-3 py-2 rounded-lg border border-border/50 bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20"
            autoFocus
            disabled={isLoading}
          />
        </div>
        
        <div>
          <label className="text-sm font-medium text-foreground block mb-1">
            {t('library.playlistDialog.descriptionLabel', 'Description (optional)')}
          </label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('library.playlistDialog.descriptionPlaceholder', 'Add a description...')}
            className="w-full px-3 py-2 rounded-lg border border-border/50 bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20"
            disabled={isLoading}
          />
        </div>
        
        <div className="flex justify-end space-x-2 pt-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onCancel}
            disabled={isLoading}
          >
            {t('library.playlistDialog.cancel', 'Cancel')}
          </Button>
          <Button
            type="submit"
            size="sm"
            disabled={!name.trim() || isLoading}
          >
            {isLoading
              ? t('library.playlistDialog.creating', 'Creating...')
              : t('library.playlistDialog.createAction', 'Create Playlist')}
          </Button>
        </div>
      </form>
    </motion.div>
  );
};

export const AddToPlaylistDialog: React.FC<AddToPlaylistDialogProps> = ({
  episodeId,
  episodeTitle,
  isOpen,
  onClose,
}) => {
  const { t } = useI18n();
  const { 
    playlists, 
    addEpisodeToPlaylist, 
    removeEpisodeFromPlaylist, 
    createPlaylist,
    isEpisodeInPlaylist,
    loadPlaylistEpisodes,
    hasLoadedEpisodes,
    episodes,
    isLoading: isLoadingPlaylists
  } = usePlaylists(false); // Disable auto-loading
  
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  
  // Load playlist episodes when dialog opens
  useEffect(() => {
    if (isOpen && !hasLoadedEpisodes) {
      loadPlaylistEpisodes();
    }
  }, [isOpen, hasLoadedEpisodes, loadPlaylistEpisodes]);

  const handleTogglePlaylist = async (playlistId: string) => {
    const isCurrentlyInPlaylist = isEpisodeInPlaylist(playlistId, episodeId);
    
    if (isCurrentlyInPlaylist) {
      await removeEpisodeFromPlaylist(playlistId, episodeId);
    } else {
      await addEpisodeToPlaylist(playlistId, episodeId);
    }
  };

  const handleCreatePlaylist = async (name: string, description?: string) => {
    setIsCreating(true);
    try {
      // Pass episodeId as initialEpisodeId to automatically add current episode
      const newPlaylist = await createPlaylist(name, description, episodeId);
      if (newPlaylist) {
        setShowCreateForm(false);
      }
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    setShowCreateForm(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md rounded-3xl">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            {t('library.playlistDialog.title', 'Add to Playlist')}
          </DialogTitle>
          <DialogDescription>
            {t(
              'library.playlistDialog.description',
              'Select a playlist to add this episode to, or create a new playlist.'
            )}
          </DialogDescription>
        </DialogHeader>
        
        {episodeTitle && (
          <div className="text-sm text-muted-foreground mb-4 p-3 bg-muted/30 rounded-lg">
            {t('library.playlistDialog.addingLabel', 'Adding:')}{' '}
            <span className="font-medium text-foreground">{episodeTitle}</span>
          </div>
        )}

        <div className="space-y-4 max-h-96 overflow-y-auto">
          {/* Existing Playlists */}
          {playlists.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-foreground">
                {t('library.playlistDialog.yourPlaylists', 'Your Playlists')}
              </h4>
              {playlists.map((playlist) => (
                <PlaylistItem
                  key={playlist.id}
                  playlist={playlist}
                  isSelected={isEpisodeInPlaylist(playlist.id, episodeId)}
                  onToggle={() => handleTogglePlaylist(playlist.id)}
                  episodeCount={playlist.episodes.length}
                />
              ))}
            </div>
          )}

          {/* Create New Playlist Section */}
          <div className="border-t border-border/50 pt-4">
            <AnimatePresence mode="wait">
              {!showCreateForm ? (
                <motion.div
                  key="create-button"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <Button
                    variant="outline"
                    onClick={() => setShowCreateForm(true)}
                    className="w-full justify-start"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {t('library.playlistDialog.createNew', 'Create New Playlist')}
                  </Button>
                </motion.div>
              ) : (
                <CreatePlaylistForm
                  key="create-form"
                  onSubmit={handleCreatePlaylist}
                  onCancel={() => setShowCreateForm(false)}
                  isLoading={isCreating}
                />
              )}
            </AnimatePresence>
          </div>

          {playlists.length === 0 && !showCreateForm && (
            <div className="text-center py-8">
              <ListMusic className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground mb-4">
                {t(
                  'library.playlistsEmptyTitle',
                  "You don't have any playlists yet"
                )}
              </p>
              <Button
                variant="outline"
                onClick={() => setShowCreateForm(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('library.playlistDialog.createFirst', 'Create Your First Playlist')}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
